/**
 * ClientPortalBudget - Vue simplifiee client du budget projet.
 * Affiche les montants clefs (foncier, honoraires, travaux...) sans details internes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => {
  if (n == null || n === 0) return '—';
  return Math.round(n).toLocaleString('fr-FR') + ' EUR';
};

const Row = ({ label, value, accent }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-gray-100 last:border-b-0">
    <span className="text-sm text-gray-600">{label}</span>
    <span className={`text-sm font-semibold ${accent ? 'text-[#13736D]' : 'text-gray-900'}`}>
      {fmt(value)}
    </span>
  </div>
);

const ClientPortalBudget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/works-budget`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="realease-card p-6" data-testid="client-budget-loading">
        <p className="text-sm text-gray-400 italic">Chargement de votre budget...</p>
      </div>
    );
  }

  if (error || !data?.summary) {
    return (
      <div className="realease-card p-6" data-testid="client-budget-empty">
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Budget de votre projet
        </h3>
        <p className="text-sm text-gray-400 italic">
          Votre budget projet sera affiche ici des qu'il sera valide par votre conseiller.
        </p>
      </div>
    );
  }

  const s = data.summary;
  const travauxPaidPct = s.travaux_total_ttc > 0
    ? Math.round((s.travaux_paye_ttc / s.travaux_total_ttc) * 100)
    : 0;

  return (
    <div className="realease-card p-6" data-testid="client-budget-section">
      <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2 text-lg">
        <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Budget de votre projet
      </h3>
      <p className="text-xs text-gray-500 mb-4">Recapitulatif des montants clefs de votre operation.</p>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="p-3 rounded-lg bg-[#13736D]/5 border border-[#13736D]/10">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#13736D]">Total projet TTC</p>
          <p className="text-xl font-bold text-[#13736D] mt-1" data-testid="client-budget-total">
            {fmt(s.total_project_ttc)}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-[#A57F4F]/5 border border-[#A57F4F]/10">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#A57F4F]">Montant a emprunter</p>
          <p className="text-xl font-bold text-[#A57F4F] mt-1" data-testid="client-budget-loan">
            {fmt(s.loan_amount)}
          </p>
        </div>
      </div>

      {/* Detail */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1">Acquisition</p>
          <Row label="Prix net foncier" value={s.foncier_net} />
          <Row label="Frais d'agence" value={s.frais_agence} />
          <Row label="Frais de notaire" value={s.frais_notaire} />
          <Row label="Garantie hypothecaire" value={s.garantie_hypothecaire} />
          <Row label="Frais de dossier bancaire" value={s.frais_dossier_bancaire} />
          <Row label="Frais de courtage" value={s.frais_courtage} />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-1 mt-3 md:mt-0">Accompagnement & travaux</p>
          <Row label="Honoraires RealEase" value={s.honoraires_realease} accent />
          <Row label="Travaux TTC" value={s.travaux_total_ttc} />
          <Row label="Travaux payes" value={s.travaux_paye_ttc} />
          <Row label="Travaux restant a payer" value={s.travaux_reste_a_payer_ttc} />
          <Row label="Frais additionnels" value={s.extra_fees_total} />
          <Row label="Apport personnel" value={s.apport} accent />
        </div>
      </div>

      {/* Avancement travaux */}
      {s.travaux_total_ttc > 0 && (
        <div className="mt-5">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Avancement paiements travaux</span>
            <span className="font-semibold text-[#13736D]">{travauxPaidPct}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-testid="client-budget-progress">
            <div
              className="h-full bg-gradient-to-r from-[#13736D] to-[#008A8D] transition-all"
              style={{ width: `${travauxPaidPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Interventions */}
      {data.interventions && data.interventions.length > 0 && (
        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-2">
            Intervenants travaux ({data.interventions.length})
          </p>
          <div className="space-y-1.5" data-testid="client-budget-interventions">
            {data.interventions.map((it) => (
              <div key={it.id} className="flex items-center justify-between p-2 rounded-md bg-gray-50 text-xs">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{it.intervenant}</p>
                  {it.description && <p className="text-gray-500 truncate">{it.description}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-medium text-gray-700">{fmt(it.cost_ttc)}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    it.paid
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {it.paid ? 'Paye' : 'En attente'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalBudget;
